import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../api/client.js';
import type { TimelineResponse } from '../api/types.js';
import { workOrderStatusLabel } from '../../shared/workflow.js';
import type { WorkOrderStatus } from '../../shared/workflow.js';
import { Card, EmptyState, Spinner } from '../components/ui.js';
import { formatDateTime, humanizeAction } from '../lib/format.js';

interface TimelineEntry {
  at: string;
  title: string;
  detail: string | null;
  actor: string | null;
  actorOrgType: string | null;
}

export function TimelineSection({
  workOrderId,
  kind,
}: {
  workOrderId: string;
  kind?: 'repair' | 'supplies';
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['timeline', workOrderId],
    queryFn: () => apiRequest<TimelineResponse>(`/api/work-orders/${workOrderId}/timeline`),
  });

  if (isLoading) return <Spinner label="Loading history" />;

  const entries: TimelineEntry[] = [
    ...(data?.statusChanges ?? []).map((change) => ({
      at: change.changedAt,
      title: change.fromStatus
        ? `${label(change.fromStatus, kind)} → ${label(change.toStatus, kind)}`
        : `Created as ${label(change.toStatus, kind)}`,
      detail: change.reason,
      actor: change.actorName,
      actorOrgType: change.actorOrgType,
    })),
    ...(data?.events ?? []).map((event) => ({
      at: event.occurredAt,
      title: humanizeAction(event.action),
      detail: null,
      actor: event.actorName,
      actorOrgType: event.actorOrgType,
    })),
  ].sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  return (
    <Card title="History">
      {entries.length === 0 ? (
        <EmptyState title="No history yet" description="Actions on this work order appear here." />
      ) : (
        <ol className="flex flex-col">
          {entries.map((entry, position) => (
            <li key={`${entry.at}-${position}`} className="flex gap-3 pb-4 last:pb-0">
              <div className="flex flex-col items-center">
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-600" />
                {position < entries.length - 1 && (
                  <span className="mt-1 w-px flex-1 bg-slate-200" aria-hidden="true" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900">{entry.title}</p>
                <p className="text-xs text-slate-600">
                  {formatDateTime(entry.at)}
                  {entry.actor ? ` · ${entry.actor}` : ''}
                  {entry.actorOrgType
                    ? ` (${entry.actorOrgType === 'customer' ? 'USPS' : 'VGS'})`
                    : ''}
                </p>
                {entry.detail && (
                  <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{entry.detail}</p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function label(status: string, kind?: 'repair' | 'supplies'): string {
  return workOrderStatusLabel(status as WorkOrderStatus, kind);
}
